import React, { useEffect, useContext, useRef } from "react";
import { InputContainer } from "uikit-react";
import classnames from "classnames";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { AppContexts } from "../../providers";
import { Form, FormRow, FormInputWrapper } from "../../ui";
import UIkit from "uikit";

const schema = yup.object().shape({
  question: yup.string().trim().required("Question is required"),
  answer: yup.string().trim().required("Answer is required"),
});

const AddToFaqModal = ({ onSuccess }) => {
  const { resolve, loading } = useContext(AppContexts.ChatbotContext);
  const offCanvasRef = useRef(null);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { answer: "" },
  });

  useEffect(() => {
    // Initialize UIkit offcanvas
    if (offCanvasRef.current) {
      const offcanvas = UIkit.offcanvas(offCanvasRef.current);

      // Add event listeners
      const showHandler = () => {
        if (window.editingQuestion) {
          setValue("question", window.editingQuestion.question);
        }
      };

      const hideHandler = () => {
        reset();
        window.editingQuestion = null;
      };

      UIkit.util.on(offCanvasRef.current, "shown", showHandler);
      UIkit.util.on(offCanvasRef.current, "hidden", hideHandler);

      return () => {
        // Cleanup
        UIkit.util.off(offCanvasRef.current, "shown", showHandler);
        UIkit.util.off(offCanvasRef.current, "hidden", hideHandler);
        if (offcanvas) {
          offcanvas.hide();
        }
      };
    }
  }, [setValue, reset]);

  const onSubmit = async (data) => {
    try {
      await resolve(
        window.editingQuestion.id,
        data.question.trim(),
        data.answer.trim(),
      );
      UIkit.notification({
        message: "FAQ created and question marked as resolved",
        status: "success",
      });
      UIkit.offcanvas(offCanvasRef.current).hide();
      if (onSuccess) onSuccess();
    } catch (error) {
      UIkit.notification({
        message: error.response?.status === 409
          ? "This question has already been resolved or is no longer available. Refresh the list."
          : "Failed to add to FAQ. Refresh the list before trying again.",
        status: "danger",
      });
    }
  };

  return (
    <div
      id="add-unanswered-to-faq-modal"
      ref={offCanvasRef}
      className="uk-modal-full hms-modals"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close="true"
          />
          <div className="uk-modal-header">
            <h2 className="uk-modal-title">Add Unanswered Question to FAQ</h2>
          </div>
          <div className="uk-modal-body uk-flex">
            <Form
              className="uk-grid-small"
              uk-grid="true"
              onSubmit={handleSubmit(onSubmit)}
            >
              <FormRow>
                <FormInputWrapper label="Question*" size="two">
                  <InputContainer>
                    <input
                      className="uk-input"
                      type="text"
                      aria-label="Question"
                      {...register("question")}
                    />
                  </InputContainer>
                  {errors.question && (
                    <div className="uk-label-danger">{errors.question.message}</div>
                  )}
                </FormInputWrapper>
              </FormRow>
              <FormRow>
                <FormInputWrapper label="Answer*" size="two">
                  <InputContainer>
                    <textarea
                      className={classnames("uk-textarea", {
                        error: errors.answer,
                      })}
                      rows="5"
                      aria-label="Answer"
                      placeholder="Enter the answer"
                      {...register("answer")}
                    />
                  </InputContainer>
                  {errors.answer && (
                    <div className="uk-label-danger">
                      {errors.answer.message}
                    </div>
                  )}
                </FormInputWrapper>
              </FormRow>
              <div className="uk-margin-top">
                <button
                  className="uk-button hms-btn hms-red-btn"
                  type="submit"
                  disabled={loading || isSubmitting}
                >
                  {loading || isSubmitting ? (
                    <div uk-spinner="ratio: 0.6"></div>
                  ) : (
                    "Add to FAQ"
                  )}
                </button>
                <button
                  className="uk-button hms-btn uk-offcanvas-close"
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddToFaqModal;
